import { useCallback, useEffect, useState } from 'react';
import {
  addConcernNote,
  closeConcern,
  getBaselines,
  getConcerns,
  getMetrics,
  type AssistantNote,
  type Baseline,
  type Concern,
  type ConcernNote,
  type EpisodeSummary,
  type MetricPoint,
} from '../api.js';
import SymptomInput from '../components/SymptomInput.js';
import SeveritySeg from '../components/SeveritySeg.js';
import FeelingOffModal from '../components/FeelingOffModal.js';
import { metricMeta, slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { relativeAgo } from '../lib/freshness.js';
import {
  concernDuration,
  confidenceDots,
  latestSeverity,
  mergeTimeline,
  severitySlot,
  sinceOnsetDelta,
} from '../lib/concerns.js';
import { fmtNumber } from '../lib/format.js';
import { Button, Card, EmptyState, Group, Icon, IconButton, IconTile, Page, Row, Sheet } from '../ui/index.js';
import { deviationTone, type DeviationDirection } from '../lib/baseline.js';

/**
 * Palette slot the assistant wears on the concern timeline — indigo (slot 5), a
 * calm hue not used by the severity dots (2,3,6,7,8) so an assessment reads as a
 * distinct voice rather than another severity rating.
 */
const ASSISTANT_SLOT = 5;

/** Vitals scored against the personal baseline while a concern is open. */
const SINCE_METRICS: { type: string; direction: DeviationDirection }[] = [
  { type: 'resting_hr', direction: 'higher-worse' },
  { type: 'hrv_sdnn', direction: 'higher-better' },
];

/**
 * "Resting HR +3 vs your norm" chips for an open concern: each vital's average
 * since the episode opened, compared to its rolling baseline. Chips appear only
 * once there are ≥3 since-onset days (sinceOnsetDelta enforces this) so a fresh
 * concern doesn't show noise.
 */
function SinceOnsetChips({
  openedAt,
  baselines,
}: {
  openedAt: string;
  baselines: Record<string, Baseline | null>;
}): JSX.Element | null {
  const [points, setPoints] = useState<Record<string, MetricPoint[]> | null>(null);

  useEffect(() => {
    let alive = true;
    const to = new Date().toISOString();
    Promise.all(
      SINCE_METRICS.map((m) =>
        getMetrics({ type: m.type, from: openedAt, to, bucket: 'day', agg: 'avg' }).then(
          (r) => [m.type, r.points] as const,
        ),
      ),
    )
      .then((entries) => {
        if (alive) setPoints(Object.fromEntries(entries));
      })
      .catch(() => {
        if (alive) setPoints({});
      });
    return () => {
      alive = false;
    };
  }, [openedAt]);

  if (!points) return null;

  const chips = SINCE_METRICS.flatMap(({ type, direction }) => {
    const baseline = baselines[type] ?? null;
    const dev = sinceOnsetDelta(points[type] ?? [], baseline);
    if (!dev || !baseline) return [];
    const tone = baseline.sd > 0 ? deviationTone(dev.delta / baseline.sd, direction) : 'neutral';
    return [{ type, label: `${metricMeta(type).label} ${dev.label}`, tone }];
  });

  if (chips.length === 0) return null;
  return (
    <div className="cn-since">
      {chips.map((ch) => (
        <span key={ch.type} className={`hb-pill${ch.tone === 'warn' ? ' hb-pill--warn' : ch.tone === 'good' ? ' hb-pill--good' : ''}`}>
          {ch.label}
        </span>
      ))}
    </div>
  );
}

function noteTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** The 32px leading slot: a severity-coloured dot (grey when unrated). */
function SevDot({ severity }: { severity: number | null }): JSX.Element {
  const mode = useThemeMode();
  return (
    <span
      className="cn-sev"
      style={{ background: severity !== null ? slotColor(severitySlot(severity), mode) : 'var(--fill-strong)' }}
      title={severity !== null ? `Severity ${severity}/5` : 'No severity'}
    >
      {severity ?? ''}
    </span>
  );
}

/**
 * One concern's timeline as a grouped list, newest first, interleaving
 * hand-written notes with the assistant's cited assessments. mergeTimeline
 * yields ascending order; we reverse for newest-first.
 */
function NoteTimeline({
  notes,
  assessments,
  header,
}: {
  notes: ConcernNote[];
  assessments: AssistantNote[];
  header?: string;
}): JSX.Element | null {
  const mode = useThemeMode();
  const ordered = mergeTimeline(notes, assessments).reverse();
  return (
    <Group header={header} className="cn-timeline">
      {ordered.map((e) =>
        e.kind === 'note' ? (
          <Row
            key={`n-${e.item.id}`}
            className="cn-note"
            leading={<SevDot severity={e.item.severity} />}
            title={
              e.item.symptoms.length > 0
                ? e.item.symptoms.map((s) => s.name).join(', ')
                : e.item.text ?? (e.item.severity !== null ? `Severity ${e.item.severity}/5` : 'Note')
            }
            sub={
              <>
                {e.item.symptoms.length > 0 && e.item.text && <span className="cn-note__text">{e.item.text}</span>}
                <span className="cn-note__time">
                  {noteTime(e.item.notedAt)}
                  {e.item.severity !== null ? ` · severity ${e.item.severity}/5` : ''}
                </span>
              </>
            }
          />
        ) : (
          <Row
            key={`a-${e.item.id}`}
            className="cn-assess"
            leading={<IconTile name="bot" color={slotColor(ASSISTANT_SLOT, mode)} />}
            title={<span className="cn-assess__label">Assistant</span>}
            sub={
              <>
                {e.item.body && <span className="cn-assess__body">{e.item.body}</span>}
                {e.item.citations.length > 0 && (
                  <span className="cn-cites">
                    {e.item.citations.map((c, i) => (
                      <span className="hb-pill" key={`${c.tool}-${i}`} title={c.summary}>
                        {c.tool}
                      </span>
                    ))}
                  </span>
                )}
                <span className="cn-note__time">{noteTime(e.item.createdAt)}</span>
              </>
            }
          />
        ),
      )}
    </Group>
  );
}

/** "3.5 days" / "1 day" from a fractional day count. */
function durationLabel(days: number): string {
  const rounded = Number(days.toFixed(1));
  return `${fmtNumber(rounded)} ${rounded === 1 ? 'day' : 'days'}`;
}

/**
 * The assistant's closing summary of a resolved episode: narrative, symptom
 * chips, contributing factors with confidence dots, and duration.
 */
function EpisodeSummaryBlock({ summary }: { summary: EpisodeSummary }): JSX.Element {
  return (
    <Card eyebrow="Episode summary" className="cn-summary">
      <p className="cn-summary__narrative">{summary.narrative}</p>
      {summary.symptoms.length > 0 && (
        <div className="cn-since">
          {summary.symptoms.map((s) => (
            <span key={s} className="hb-pill">
              {s}
            </span>
          ))}
        </div>
      )}
      {summary.contributors.length > 0 && (
        <ul className="cn-contrib">
          {summary.contributors.map((c, i) => {
            const filled = confidenceDots(c.confidence);
            return (
              <li key={`${c.factor}-${i}`}>
                <span className="cn-conf" title={`${c.confidence} confidence`} aria-label={`${c.confidence} confidence`}>
                  {[1, 2, 3].map((d) => (
                    <span key={d} className={`cn-conf__dot${d <= filled ? ' cn-conf__dot--on' : ''}`} />
                  ))}
                </span>
                <span>{c.factor}</span>
              </li>
            );
          })}
        </ul>
      )}
      {(summary.durationDays !== null || summary.outcome) && (
        <p className="hb-hint cn-summary__meta">
          {[summary.durationDays !== null ? durationLabel(summary.durationDays) : null, summary.outcome]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </Card>
  );
}

/** "Add a note" — symptoms, severity, text — in a sheet. */
function AddNoteSheet({
  concern,
  onClose,
  onAdded,
}: {
  concern: Concern;
  onClose: () => void;
  onAdded: () => void;
}): JSX.Element {
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [severity, setSeverity] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = symptoms.length > 0 || severity !== null || text.trim().length > 0;

  function save(): void {
    if (!canSave || busy) return;
    setBusy(true);
    setError(null);
    addConcernNote(concern.id, {
      text: text.trim() || undefined,
      symptoms: symptoms.length > 0 ? symptoms : undefined,
      severity: severity ?? undefined,
    })
      .then(() => {
        setSymptoms([]);
        setSeverity(null);
        setText('');
        onAdded();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not add note.'))
      .finally(() => setBusy(false));
  }

  return (
    <Sheet
      title={concern.title}
      onClose={onClose}
      action={{ label: busy ? 'Saving…' : 'Add', onClick: save, disabled: !canSave || busy }}
    >
      <div className="hb-form">
        <div className="hb-field">
          <span>How bad, 1–5?</span>
          <SeveritySeg value={severity} onChange={setSeverity} />
        </div>
        <div className="hb-field">
          <span>Symptoms</span>
          <SymptomInput values={symptoms} onChange={setSymptoms} />
        </div>
        <label className="hb-field">
          <span>Note</span>
          <textarea rows={3} placeholder="What changed?" value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        {error && <p className="hb-form__error">{error}</p>}
      </div>
    </Sheet>
  );
}

/** "How did it resolve?" — optional outcome, then close the episode. */
function ResolveSheet({
  concern,
  onClose,
  onResolve,
}: {
  concern: Concern;
  onClose: () => void;
  onResolve: (outcome: string) => Promise<void>;
}): JSX.Element {
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const go = (): void => {
    setBusy(true);
    void onResolve(outcome).finally(() => setBusy(false));
  };
  return (
    <Sheet title="Resolve" onClose={onClose} action={{ label: busy ? 'Saving…' : 'Resolve', onClick: go, disabled: busy }}>
      <div className="hb-form">
        <p className="hb-hint">{concern.title} — open {concernDuration(concern.openedAt, null)}.</p>
        <label className="hb-field">
          <span>How did it resolve? (optional)</span>
          <input
            value={outcome}
            autoFocus
            placeholder="Cleared up on its own"
            onChange={(e) => setOutcome(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') go();
            }}
          />
        </label>
      </div>
    </Sheet>
  );
}

export default function Concerns(): JSX.Element {
  const mode = useThemeMode();
  const [concerns, setConcerns] = useState<Concern[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState<Concern | null>(null);
  const [noting, setNoting] = useState<Concern | null>(null);
  const [resolving, setResolving] = useState<Concern | null>(null);
  const [creating, setCreating] = useState(false);
  const [baselines, setBaselines] = useState<Record<string, Baseline | null>>({});

  useEffect(() => {
    let alive = true;
    getBaselines(SINCE_METRICS.map((m) => m.type))
      .then((b) => alive && setBaselines(b))
      .catch(() => {
        /* non-fatal: chips just won't render */
      });
    return () => {
      alive = false;
    };
  }, []);

  const reload = useCallback(() => {
    getConcerns('all')
      .then((rows) => {
        setConcerns(rows);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load concerns.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => reload(), [reload]);

  async function close(id: string, outcome: string): Promise<void> {
    try {
      await closeConcern(id, outcome.trim() || undefined);
      setResolving(null);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not close concern.');
    }
  }

  const open = concerns.filter((c) => c.closedAt === null);
  const closed = concerns.filter((c) => c.closedAt !== null);
  const sub =
    open.length > 0
      ? `${open.length} open${closed.length > 0 ? ` · ${closed.length} resolved` : ''}`
      : closed.length > 0
        ? `All clear · ${closed.length} resolved`
        : 'Health episodes';

  return (
    <Page
      title="Concerns"
      sub={sub}
      className="cn"
      actions={<IconButton icon="plus" label="New concern" onClick={() => setCreating(true)} />}
    >
      {creating && (
        <FeelingOffModal
          concernId={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            reload();
          }}
        />
      )}

      {error && (
        <p className="hb-form__error cn-error" role="alert">
          {error}
        </p>
      )}

      {loading && (
        <div className="ui-group" aria-hidden="true">
          <div className="hb-skelrow" />
          <div className="hb-skelrow" />
          <div className="hb-skelrow" />
        </div>
      )}

      {!loading && open.length === 0 && (
        <div className="ui-group cn-clear">
          <EmptyState
            icon="check"
            title={closed.length > 0 ? 'Nothing open' : 'Nothing on your mind'}
            hint="When something feels off, note it here and track it until it's gone."
            action={
              <Button variant="secondary" icon="plus" onClick={() => setCreating(true)}>
                Feeling off?
              </Button>
            }
          />
        </div>
      )}

      {open.map((c) => {
        const sev = latestSeverity(c.notes);
        return (
          <div key={c.id} className="cn-open">
            <Card
              eyebrow={
                <>
                  <span className="ui-dot" style={{ background: sev !== null ? slotColor(severitySlot(sev), mode) : 'var(--warn)' }} />
                  Open · {concernDuration(c.openedAt, null)}
                </>
              }
              title={c.title}
              accent={sev !== null ? slotColor(severitySlot(sev), mode) : undefined}
              actions={
                <>
                  <Button icon="plus" onClick={() => setNoting(c)}>
                    Add note
                  </Button>
                  <Button variant="secondary" onClick={() => setResolving(c)}>
                    Resolve
                  </Button>
                </>
              }
            >
              <p className="cn-open__meta">
                {sev !== null ? `Latest severity ${sev}/5 · ` : ''}opened {relativeAgo(c.openedAt)}
              </p>
              <SinceOnsetChips openedAt={c.openedAt} baselines={baselines} />
            </Card>
            <NoteTimeline header="Timeline" notes={c.notes} assessments={c.assessments} />
          </div>
        );
      })}

      <Group header="Resolved" id="cn-history">
        {closed.map((c) => (
          <Row
            key={c.id}
            leading={<Icon name="check" size={20} className="cn-donecheck" />}
            title={c.title}
            sub={c.outcome ?? undefined}
            detail={concernDuration(c.openedAt, c.closedAt)}
            onClick={() => setViewing(c)}
          />
        ))}
      </Group>

      {noting && (
        <AddNoteSheet
          concern={noting}
          onClose={() => setNoting(null)}
          onAdded={() => {
            setNoting(null);
            reload();
          }}
        />
      )}
      {resolving && (
        <ResolveSheet concern={resolving} onClose={() => setResolving(null)} onResolve={(o) => close(resolving.id, o)} />
      )}
      {viewing && (
        <Sheet title={viewing.title} onClose={() => setViewing(null)} className="cn-sheet">
          <p className="hb-hint cn-sheet__sub">
            {concernDuration(viewing.openedAt, viewing.closedAt)}
            {viewing.outcome ? ` · ${viewing.outcome}` : ''}
          </p>
          {viewing.summary && <EpisodeSummaryBlock summary={viewing.summary} />}
          <NoteTimeline notes={viewing.notes} assessments={viewing.assessments} />
          {viewing.notes.length === 0 && viewing.assessments.length === 0 && !viewing.summary && (
            <p className="hb-hint">No notes were added.</p>
          )}
        </Sheet>
      )}
    </Page>
  );
}
